import React, { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Alert, Badge, Button, Form } from 'react-bootstrap';
import DataTable from './components/DataTable';
import Loading from './components/Loading';
import useAdminData from './useAdminData';
import { formatDate, formatMoney } from './format';

const COLUMNS = [
  { key: 'id', label: '#', sortable: true },
  { key: 'name', label: 'Name', sortable: true, value: c => `${c.lastName}, ${c.firstName}` },
  { key: 'emailAddress', label: 'Email', sortable: true },
  { key: 'phoneNumber', label: 'Phone' },
  { key: 'itemCount', label: 'Items', sortable: true },
  { key: 'itemValue', label: 'Value', sortable: true, render: c => formatMoney(c.itemValue) },
  {
    key: 'vendor',
    label: 'Vendor',
    sortable: true,
    value: c => (c.isVendor ? `vendor ${c.vendorId || ''}`.trim() : ''),
    render: c => (c.isVendor ? <Badge variant="info">{c.vendorId || 'Vendor'}</Badge> : null)
  },
  { key: 'createdAt', label: 'Signed up', sortable: true, render: c => formatDate(c.createdAt) }
];

export default function ConsignerList() {
  const history = useHistory();
  const location = useLocation();
  const { data, error, loading, waking, reload } = useAdminData('/admin/consigners');

  const emptyOnly = new URLSearchParams(location.search).get('show') === 'empty';
  const rows = useMemo(
    () => (data || []).filter(c => !emptyOnly || c.itemCount === 0),
    [data, emptyOnly]
  );

  const toggleEmptyOnly = event => {
    history.replace({ search: event.target.checked ? '?show=empty' : '' });
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <h2 className="mb-0">Consigners</h2>
        {data && <span className="ml-3 text-muted">{rows.length} shown</span>}
        <Button variant="outline-secondary" size="sm" className="ml-auto" onClick={reload} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && !data && <Loading waking={waking} />}

      {data && (
        <>
          <Form.Check
            id="consigners-empty-only"
            type="checkbox"
            className="mb-2"
            label="Only consigners with no items"
            checked={emptyOnly}
            onChange={toggleEmptyOnly}
          />
          <DataTable
            columns={COLUMNS}
            rows={rows}
            searchable
            searchPlaceholder="Search by name, email, or number"
            onRowClick={consigner => history.push(`/admin/consigners/${consigner.id}`)}
            emptyMessage={emptyOnly ? 'Every consigner has at least one item' : 'No consigners yet'}
          />
        </>
      )}
    </div>
  );
}
